import { TimeoutOptions } from "./TimeoutOptions";

/**
 */
export class Timeout {
  /**
   * Active timeout references.
   */
  public static activeTimeouts: Timeout[] = [];

  /**
   * Number ID for referencing this timeout.
   */
  public id: number;

  /**
   * Group name for applying batch actions to timeouts
   * with a given group name.
   */
  public group?: string | null;

  /**
   * The duration of the timeout.
   */
  public duration: number;

  /**
   * Pause state of the timeout.
   */
  public paused: boolean;

  /**
   * Callback function.
   */
  private cb: Function;

  /**
   * Optional context to bind onto callback.
   */
  private ctx?: any;

  /**
   * The time the tween was started for tracking elapsed time.
   */
  private startTime: number;

  /**
   * Remaining duration for a paused timeout.
   */
  private remainingTime: number;

  /**
   *
   * @param options - TimeoutOptions object.
   */
  constructor(options: TimeoutOptions) {
    options = options || ({} as TimeoutOptions);
    this.duration = options.duration || 0;
    this.group = options.group || "";
    this.ctx = options.ctx || null;
    this.cb = options.cb || null;
    this.start();
  }

  /**
   * @param timeout - timeout instance to add.
   */
  private static addActiveTimeout(timeout: Timeout) {
    Timeout.activeTimeouts.push(timeout);
  }

  /**
   * @param timeout - timeout instance to remove.
   */
  private static removeActiveTimeout(timeout: Timeout) {
    for (let i = 0; i < Timeout.activeTimeouts.length; i++)
      if (timeout.id === Timeout.activeTimeouts[i].id)
        Timeout.activeTimeouts.splice(i, 1);
  }

  /**
   * @param id - Optional group ID of the timeouts to pause.
   */
  public static pauseAll(group?: string) {
    for (let i = 0; i < this.activeTimeouts.length; i++) {
      const timeout = this.activeTimeouts[i];
      if (group) {
        if (timeout.group === group) timeout.pause();
      } else timeout.pause();
    }
  }

  /**
   * @param id - Optional group ID of the timeouts to resume.
   */
  public static resumeAll(group?: string) {
    for (let i = 0; i < this.activeTimeouts.length; i++) {
      const timeout = this.activeTimeouts[i];
      if (group) {
        if (timeout.group === group) timeout.pause();
      } else timeout.resume();
    }
  }

  /**
   * @param id - Optional group ID of the timeouts to clear.
   */
  public static clearAll(group?: string | null) {
    for (let i = 0; i < this.activeTimeouts.length; i++) {
      const timeout = this.activeTimeouts[i];
      if (group) {
        if (timeout.group === group) {
          this.activeTimeouts.splice(i, 1);
          window.clearTimeout(timeout.id);
          i--;
        }
      } else timeout.clear();
    }
  }

  /**
   * Start the timeout.
   */
  public start() {
    this.clear();
    const duration = this.paused ? this.remainingTime : this.duration;
    this.startTime = new Date().getTime() / 1000;
    this.id = window.setTimeout(() => {
      Timeout.removeActiveTimeout(this);
      delete this.id;
      if (this.cb) {
        if (this.ctx) this.cb.call(this.ctx);
        else this.cb();
      }
    }, duration * 1000);
    Timeout.addActiveTimeout(this);
    delete this.remainingTime;
    this.paused = false;
  }

  /**
   * Pause timeout.
   */
  public pause() {
    if (!this.paused) {
      this.clear();
      const currentTime = new Date().getTime() / 1000;
      const elapsedTime = currentTime - this.startTime;
      this.remainingTime = this.duration - elapsedTime;
      this.paused = true;
    }
  }

  /**
   * Pause timeout.
   */
  public resume() {
    if (this.paused) this.start();
    this.paused = false;
  }

  /**
   * Clear timeout.
   */
  public clear() {
    if (typeof this.id !== "undefined") {
      Timeout.removeActiveTimeout(this);
      window.clearTimeout(this.id);
    }
    this.paused = false;
  }
}
